import { useEffect, useState } from "react";
import {
  Form,
  useActionData,
  useNavigation,
  Link,
  useSearchParams,
  Navigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Button } from "../../components/common";
import { LanguageSwitcher } from "../../components/common/LanguageSwitcher";
import { prereleaseAPI } from "../../lib/api";
import type { ActionResult } from "../../routes/actions";
import "./auth.css";

export default function RegisterPage() {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const actionData = useActionData() as ActionResult | undefined;
  const isSubmitting = navigation.state === "submitting";
  const [searchParams] = useSearchParams();
  const [blocked, setBlocked] = useState<boolean | null>(null);
  const bypassToken = searchParams.get("bypass");

  useEffect(() => {
    prereleaseAPI
      .getConfig()
      .then((res) => {
        if (res.prereleaseMode && !bypassToken) {
          setBlocked(true);
        } else {
          setBlocked(false);
        }
      })
      .catch(() => setBlocked(false));
  }, [bypassToken]);

  useEffect(() => {
    if (actionData && !actionData.success && actionData.error) {
      toast.error(actionData.error);
    }
  }, [actionData]);

  if (blocked === null) return null;
  if (blocked) return <Navigate to="/" replace />;

  return (
    <div className="auth-container">
      <div className="auth-language-switcher">
        <LanguageSwitcher variant="standalone" />
      </div>
      <div className="auth-card">
        <div className="auth-header">
          <h1>{t("auth.register.title")}</h1>
          <p>{t("auth.register.subtitle")}</p>
        </div>

        <Form method="post" className="auth-form">
          <div className={`form-group ${actionData?.fieldErrors?.email ? "error" : ""}`}>
            <label htmlFor="email">{t("auth.register.emailLabel")}</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder={t("auth.register.emailPlaceholder")}
              autoComplete="email"
            />
            {actionData?.fieldErrors?.email && (
              <span className="form-error">{actionData.fieldErrors.email}</span>
            )}
          </div>

          <div className={`form-group ${actionData?.fieldErrors?.password ? "error" : ""}`}>
            <label htmlFor="password">{t("auth.register.passwordLabel")}</label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder={t("auth.register.passwordPlaceholder")}
              autoComplete="new-password"
            />
            {actionData?.fieldErrors?.password && (
              <span className="form-error">{actionData.fieldErrors.password}</span>
            )}
          </div>

          <div className={`form-group ${actionData?.fieldErrors?.confirmPassword ? "error" : ""}`}>
            <label htmlFor="confirmPassword">{t("auth.register.confirmPasswordLabel")}</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder={t("auth.register.confirmPasswordPlaceholder")}
              autoComplete="new-password"
            />
            {actionData?.fieldErrors?.confirmPassword && (
              <span className="form-error">{actionData.fieldErrors.confirmPassword}</span>
            )}
          </div>

          <div
            className={`form-group form-group-optional ${actionData?.fieldErrors?.organizationName ? "error" : ""}`}
          >
            <label htmlFor="organizationName">{t("auth.register.organizationLabel")}</label>
            <input
              id="organizationName"
              name="organizationName"
              type="text"
              placeholder={t("auth.register.organizationPlaceholder")}
              autoComplete="organization"
            />
            {actionData?.fieldErrors?.organizationName && (
              <span className="form-error">{actionData.fieldErrors.organizationName}</span>
            )}
          </div>

          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={isSubmitting}
            loadingText={t("auth.register.submitting")}
          >
            {t("auth.register.submitButton")}
          </Button>
        </Form>

        <div className="auth-links">
          <Link to="/login" className="auth-link">
            {t("auth.register.hasAccount")}
          </Link>
        </div>
      </div>
    </div>
  );
}
